import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { isAthlete, isGuardian, isPolaris, setHasProfile } from "../utils/auth";
import { isPostLoginNextAllowed, loginRoleFromSources, resolvePostLoginPath, takePostLoginPath } from "../utils/loginRedirect";
import { resolveGuardianPostLoginPath } from "../utils/viewedAthlete";
import { useAuth } from "../contexts/AuthContext";

function useQuery() {
    const { search } = useLocation();
    return new URLSearchParams(search);
}

/**
 * Handles the Clever OAuth 2.0 frontend landing page.
 *
 * Flow:
 *   1. Backend completes the full OAuth flow (code exchange, user provisioning, JWT cookie)
 *   2. Backend redirects the browser here with session metadata as query params:
 *      /auth/clever/callback?userId=...&role=...&hasProfile=...&authProvider=CLEVER
 *   3. This component reads those params, writes non-sensitive data to localStorage,
 *      calls refreshAuth() to validate the JWT cookie, then navigates to the dashboard.
 *   4. On missing userId (error case): redirects to /login?error=clever_auth_failed
 */
export default function CleverLoginCallback() {
    const q = useQuery();
    const navigate = useNavigate();
    const { refreshAuth } = useAuth();
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        async function handleCallback() {
            const userId = q.get("userId");
            const role = q.get("role");
            const hasProfile = q.get("hasProfile") === "true";
            const authProvider = q.get("authProvider");

            if (!userId) {
                navigate("/login?error=clever_auth_failed", { replace: true });
                return;
            }

            localStorage.setItem("loggedInUserId", userId);
            localStorage.setItem("userId", userId);
            localStorage.setItem("authProvider", authProvider?.toLowerCase() ?? "clever");

            if (role) {
                localStorage.setItem("userRole", role);
            }

            setHasProfile(hasProfile);

            try {
                await refreshAuth();
            } catch (err) {
                console.error("Failed to refresh auth after Clever login:", err);
                navigate("/login?error=auth_refresh_failed", { replace: true });
                return;
            }

            navigateByRole(role, hasProfile);
        }

        handleCallback();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (error) {
        return (
            <main style={{ display: "grid", placeItems: "center", height: "100dvh" }}>
                <div className="flex flex-col items-center justify-center gap-4 max-w-md text-center px-4">
                    <h2 className="text-2xl font-semibold text-red-600">Login Failed</h2>
                    <p className="text-gray-700">{error}</p>
                    <a
                        href="/login"
                        className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Return to Login
                    </a>
                </div>
            </main>
        );
    }

    return (
        <main style={{ display: "grid", placeItems: "center", height: "100dvh" }}>
            <div className="flex flex-col items-center justify-center">
                <h2 className="text-2xl font-semibold mb-2">Authenticating with Clever...</h2>
                <p className="text-gray-600">Please wait while we complete your login.</p>
            </div>
        </main>
    );

    function navigateByRole(role: string | null, hasProfile: boolean) {
        const next = takePostLoginPath();
        const resolvedRole = loginRoleFromSources({
            role,
            isGuardian: isGuardian(),
            isPolaris: isPolaris(),
            isAthlete: isAthlete(),
        });

        if (resolvedRole === "GUARDIAN") {
            navigate(resolveGuardianPostLoginPath(next), { replace: true });
            return;
        }

        if (resolvedRole === "ATHLETE" && !hasProfile) {
            if (next && isPostLoginNextAllowed("ATHLETE", next)) {
                navigate(next, { replace: true });
                return;
            }
            navigate("/athletes/new", { replace: true, state: { firstTimeSetup: true } });
            return;
        }

        const path = resolvePostLoginPath(resolvedRole, next, hasProfile);
        if (path === "/add-admin-profile") {
            navigate(path, { replace: true, state: { firstTimeSetup: true } });
            return;
        }
        navigate(path, { replace: true });
    }
}
